import type { BodyHandle, Contact } from "./types";

const DAMAGE_PER_HP_J = 12_500;
const POSITION_CORRECTION = 0.5;
const POSITION_SLOP = 1e-3;

/**
 * Returns eDiss = 0.5 · μ · vClosing² · (1 − e²), the only quantity
 * downstream uses for damage (`kineticHP`) and heat (`collisionHeatJ`).
 */
export function resolveContact(c: Contact, e: number): number {
  const { a, b, nx, ny, vClosing } = c;
  if (vClosing <= 0) {
    applyPositionCorrection(c);
    return 0;
  }

  const rAx = c.cx - a.pos.x;
  const rAy = c.cy - a.pos.y;
  const rBx = c.cx - b.pos.x;
  const rBy = c.cy - b.pos.y;
  const invMA = a.mass > 0 ? 1 / a.mass : 0;
  const invMB = b.mass > 0 ? 1 / b.mass : 0;
  const invIA = a.inertia > 0 ? 1 / a.inertia : 0;
  const invIB = b.inertia > 0 ? 1 / b.inertia : 0;
  const rAcN = rAx * ny - rAy * nx;
  const rBcN = rBx * ny - rBy * nx;
  const denom = invMA + invMB + rAcN * rAcN * invIA + rBcN * rBcN * invIB;
  if (denom <= 0) return 0;

  const J = (1 + e) * vClosing / denom;
  a.vel.x -= J * nx * invMA;
  a.vel.y -= J * ny * invMA;
  b.vel.x += J * nx * invMB;
  b.vel.y += J * ny * invMB;
  if (invIA > 0) a.angVel -= J * rAcN * invIA;
  if (invIB > 0) b.angVel += J * rBcN * invIB;

  const mu = 1 / denom;
  const E = 0.5 * mu * vClosing * vClosing;
  const eDiss = E * (1 - e * e);

  applyPositionCorrection(c);
  return eDiss;
}

function applyPositionCorrection(c: Contact): void {
  const { a, b, nx, ny } = c;
  const depth = c.depth - POSITION_SLOP;
  if (depth <= 0) return;
  const invMA = a.mass > 0 ? 1 / a.mass : 0;
  const invMB = b.mass > 0 ? 1 / b.mass : 0;
  const invSum = invMA + invMB;
  if (invSum <= 0) return;
  const corr = (depth * POSITION_CORRECTION) / invSum;
  a.pos.x -= nx * corr * invMA;
  a.pos.y -= ny * corr * invMA;
  b.pos.x += nx * corr * invMB;
  b.pos.y += ny * corr * invMB;
}

export function kineticHP(body: BodyHandle, energyJ: number): number {
  if (energyJ <= 0) return 0;
  return (energyJ * body.toughness) / DAMAGE_PER_HP_J;
}
